import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  Users,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Check,
  Play,
  Loader2,
  Mic,
  MessageSquare,
  Phone,
  Video,
  Lock,
  RefreshCw,
  Briefcase,
  DollarSign,
  Award,
  Shield,
  Lightbulb,
  Target,
  Clock,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

// Icon mapping for categories (using underscore IDs to match backend)
const CATEGORY_ICONS = {
  'client_mastery': Briefcase,
  'candidate_mastery': Users,
  'negotiation_mastery': DollarSign,
  'executive_communication': Award,
  'compliance_mastery': Shield,
  'creative_outreach': Lightbulb
};

// Difficulty badge colors
const DIFFICULTY_COLORS = {
  easy: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  hard: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
};

const RPATRolePlay = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [progress, setProgress] = useState(null);
  const [settings, setSettings] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [startingScenario, setStartingScenario] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/rpat/certification/roleplay-categories');
      setCategories(res.data.categories || []);
      setProgress(res.data.progress || {});
      setSettings(res.data.settings || {});
      
      // Auto-expand first incomplete category
      const firstIncomplete = res.data.categories?.find(c => !c.is_category_complete);
      if (firstIncomplete) {
        setExpandedCategory(firstIncomplete.id);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      toast.error(err.response?.data?.detail || 'Failed to load role play categories');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSyncProgress = async () => {
    try {
      setSyncing(true);
      const res = await api.post('/rpat/certification/sync-phase-7');
      
      if (res.data.synced_count > 0) {
        toast.success(`Synced ${res.data.synced_count} scenario(s) from external sessions!`, {
          duration: 5000
        });
        
        if (res.data.phase_7_complete) {
          toast.success('Phase 7 Complete! Final Certification is now available.', {
            duration: 5000,
            action: {
              label: 'Go to Final',
              onClick: () => navigate('/rpat/certification/final')
            }
          });
        }
        
        fetchCategories();
      } else {
        toast.info(res.data.message || 'No new scenarios to sync');
      }
    } catch (err) {
      console.error('Failed to sync progress:', err);
      toast.error(err.response?.data?.detail || 'Failed to sync progress');
    } finally {
      setSyncing(false);
    }
  };

  const handleStartScenario = async (scenario, categoryId) => {
    setStartingScenario(scenario.id);
    try {
      // Navigate to the role play lab with scenario pre-selected
      // Use the correct route /roleplay/:scenarioId
      navigate(`/roleplay/${scenario.id}?from=rpat&category=${categoryId}`);
    } catch (err) {
      toast.error('Failed to start scenario');
    } finally {
      setStartingScenario(null);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  if (loading) {
    return (
      <MainLayout title="Phase 7: Role Play Lab">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  const progressPct = progress?.total_required > 0 
    ? (progress.total_completed / progress.total_required) * 100 
    : 0;
  
  const isPhaseComplete = progress?.is_phase_complete;

  return (
    <MainLayout 
      title="Phase 7: Role Play Lab"
      subtitle="Master real-world recruiting conversations through AI-powered role play scenarios"
    >
      <div className="space-y-6">
        {/* Back Navigation */}
        <Button
          variant="ghost"
          onClick={() => navigate('/rpat/certification')}
          className="mb-4"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back to Certification Dashboard
        </Button>

        {/* Phase Complete Banner */}
        {isPhaseComplete && (
          <Card className="border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-emerald-500 text-white">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-emerald-800 dark:text-emerald-200">
                      Phase 7 Complete!
                    </h3>
                    <p className="text-emerald-600 dark:text-emerald-400">
                      Average Score: {progress?.overall_average}% — You&apos;re ready for Final Certification
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/rpat/certification/final')}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Go to Final Certification
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Overall Progress Card */}
        <Card>
          <CardContent className="py-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-lg">Overall Progress</h3>
                <p className="text-sm text-muted-foreground">
                  Complete {settings?.scenarios_per_category} scenarios from each of the 6 categories
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">
                  {progress?.total_completed || 0} / {progress?.total_required || 12}
                </p>
                <p className="text-sm text-muted-foreground">Scenarios Completed</p>
              </div>
            </div>
            
            <Progress value={progressPct} className="h-3 mb-4" />
            
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-emerald-500" />
                <span>Min Score: <strong>{settings?.min_score_per_scenario}%</strong> per scenario</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-amber-500" />
                <span>Avg Required: <strong>{settings?.min_average_score}%</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-500" />
                <span>Your Avg: <strong>{progress?.overall_average || 0}%</strong></span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sync Progress Card - Show when not complete */}
        {!isPhaseComplete && (
          <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20" data-testid="phase7-sync-card">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-emerald-100 dark:bg-emerald-900/50">
                    <RefreshCw className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium">Completed Role Play sessions elsewhere?</p>
                    <p className="text-sm text-muted-foreground">
                      If you completed role play sessions in the Role Play Lab, click Sync to import progress.
                      <span className="font-medium text-emerald-600 ml-1">
                        (Min score: {settings?.min_score_per_scenario}%)
                      </span>
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleSyncProgress}
                  disabled={syncing}
                  className="border-emerald-300 hover:bg-emerald-100"
                  data-testid="sync-phase7-btn"
                >
                  {syncing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Sync Progress
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Categories */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Categories</h2>
          <p className="text-muted-foreground">
            Complete {settings?.scenarios_per_category} scenarios from each category. Click a category to view and start scenarios.
          </p>

          {categories.map((category) => {
            const CategoryIcon = CATEGORY_ICONS[category.id] || Users;
            const isExpanded = expandedCategory === category.id;
            const catProgress = (category.completed_count / category.required_count) * 100;

            return (
              <Card 
                key={category.id}
                className={`transition-all ${category.is_category_complete ? 'border-emerald-300 bg-emerald-50/30 dark:bg-emerald-950/10' : ''}`}
                data-testid={`category-${category.id}`}
              >
                {/* Category Header */}
                <CardHeader 
                  className="cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${category.is_category_complete ? 'bg-emerald-500 text-white' : 'bg-muted'}`}>
                        <CategoryIcon className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {category.name}
                          {category.is_category_complete && (
                            <Badge variant="success" className="bg-emerald-500 text-white">
                              <Check className="h-3 w-3 mr-1" />
                              Complete
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>{category.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold">
                          {category.completed_count} / {category.required_count}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Avg: {category.average_score}%
                        </p>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <Progress value={catProgress} className="h-2 mt-3" />
                </CardHeader>

                {/* Expanded Scenarios */}
                {isExpanded && (
                  <CardContent className="pt-0">
                    <Separator className="mb-4" />
                    <div className="grid gap-3">
                      {category.scenarios.map((scenario) => (
                        <div 
                          key={scenario.id}
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            scenario.is_completed 
                              ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800' 
                              : 'bg-card hover:bg-muted/30'
                          }`}
                          data-testid={`scenario-${scenario.id}`}
                        >
                          <div className="flex items-center gap-4">
                            {scenario.is_completed ? (
                              <div className="p-2 rounded-full bg-emerald-500 text-white">
                                <Check className="h-4 w-4" />
                              </div>
                            ) : (
                              <div className="p-2 rounded-full bg-muted">
                                <Play className="h-4 w-4" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{scenario.title}</p>
                              <p className="text-sm text-muted-foreground">{scenario.description}</p>
                              <div className="flex items-center gap-3 mt-1">
                                <Badge className={DIFFICULTY_COLORS[scenario.difficulty] || DIFFICULTY_COLORS.medium}>
                                  {scenario.difficulty}
                                </Badge>
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  ~{scenario.duration_minutes} min
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {scenario.is_completed ? (
                              <div className="text-right">
                                <p className="font-semibold text-emerald-600">{scenario.score}%</p>
                                <p className="text-xs text-muted-foreground">Completed</p>
                              </div>
                            ) : (
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartScenario(scenario, category.id);
                                }}
                                disabled={
                                  startingScenario === scenario.id ||
                                  (category.completed_count >= category.required_count)
                                }
                                className="min-w-[100px]"
                                data-testid={`start-${scenario.id}`}
                              >
                                {startingScenario === scenario.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : category.completed_count >= category.required_count ? (
                                  <>
                                    <Lock className="mr-2 h-4 w-4" />
                                    Done
                                  </>
                                ) : (
                                  <>
                                    <Play className="mr-2 h-4 w-4" />
                                    Start
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Category completion info */}
                    {category.is_category_complete && (
                      <div className="mt-4 p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg text-sm text-emerald-700 dark:text-emerald-300">
                        <CheckCircle2 className="h-4 w-4 inline mr-2" />
                        You&apos;ve completed this category! Average score: {category.average_score}%
                      </div>
                    )}
                    
                    {!category.is_category_complete && category.completed_count > 0 && (
                      <div className="mt-4 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                        <AlertCircle className="h-4 w-4 inline mr-2" />
                        Complete {category.required_count - category.completed_count} more scenario(s) in this category
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Requirements Summary */}
        <Card className="bg-muted/30">
          <CardContent className="py-4">
            <h3 className="font-medium mb-2">Phase 7 Requirements</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                Complete {settings?.scenarios_per_category} scenarios from each of the 6 categories ({progress?.total_required} total)
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                Score at least {settings?.min_score_per_scenario}% on each scenario
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-emerald-500" />
                Maintain an overall average of {settings?.min_average_score}% or higher
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default RPATRolePlay;
